import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { summaryCardTheme as t } from "./styles";

const formatServiceName = (name) =>
  name
    ? name.split(/[-_]/).map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()).join(" ")
    : "";

const formatCurrency = (value) =>
  typeof value === "number" && value > 0
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value)
    : "—";

function SummaryCostChart({ overall, services }) {
  const data = useMemo(() => {
    const list = Array.isArray(services) ? services : [];
    return list
      .filter((row) => (row.cost_unblended_mtd_total ?? 0) > 0 || (row.arns_cost ?? 0) > 0)
      .slice(0, 8)
      .map((row) => ({
        name: formatServiceName(row.service) || row.service || "—",
        value: row.cost_unblended_mtd_total ?? 0,
        arns: row.arns_cost ?? 0,
        fill: t.colors.success,
      }))
      .sort((a, b) => b.value - a.value);
  }, [services]);

  const totalArns = overall?.arns_cost ?? 0;

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
      <Box sx={{ ...t.chartTooltip, fontFamily: t.fontFamily }}>
        <Typography component="span" sx={{ fontWeight: 600, color: t.colors.text, fontSize: "0.75rem" }}>
          {d.name}
        </Typography>
        <Typography component="span" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", ml: 0.5, display: "block" }}>
          Cost (Last 30 Days): {formatCurrency(d.value)}
        </Typography>
        {d.arns > 0 && (
          <Typography component="span" sx={{ color: t.colors.textMuted, fontSize: "0.75rem" }}>
            ARNs: {d.arns}
          </Typography>
        )}
      </Box>
    );
  };

  if (data.length === 0) {
    return (
      <Box
        sx={{
          height: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: t.radius.lg,
          border: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.card,
          fontFamily: t.fontFamily,
        }}
      >
        <Typography variant="body2" sx={{ color: t.colors.textMuted }}>
          No Cost Insight data
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: t.colors.card,
        border: `1px solid ${t.colors.border}`,
        borderRadius: t.radius.lg,
        p: 2,
        boxShadow: t.shadow,
        fontFamily: t.fontFamily,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: t.colors.text, fontSize: "0.875rem", mb: 1, fontFamily: t.fontFamily }}>
        Cost Insight
      </Typography>
      <Typography variant="body2" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", mb: 2, fontFamily: t.fontFamily }}>
        {totalArns} ARNs with cost data
      </Typography>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={t.colors.borderLight} horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11, fill: t.colors.textMuted }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
          <YAxis type="category" dataKey="name" width={72} tick={{ fontSize: 10, fill: t.colors.text }} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: t.colors.background }} />
          <Bar dataKey="value" nameKey="name" radius={[0, 4, 4, 0]} maxBarSize={24} fill={t.colors.success} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default SummaryCostChart;
